"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
let currentToken;
const getCookie = (name) => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const regexp = new RegExp(`(?:^${name}|; *${name})=(.*?)(?:;|$)`, 'g');
    const result = regexp.exec(document.cookie);
    if (result && result[1]) {
        return result[1];
    }
    return undefined;
};
// Reformatted from https://stackoverflow.com/a/38552302
const parseJwt = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''));
    return JSON.parse(jsonPayload);
};
const setCookie = (name, value, days) => {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value}${expires}; path=/`;
};
const deleteCookie = (name) => setCookie(name, '', -1);
const parseCVAccessToken = () => {
    try {
        const CVAccessTokenCookie = getCookie('CVAccessToken');
        return CVAccessTokenCookie ? JSON.parse(CVAccessTokenCookie).access_token : undefined;
    }
    catch (error) {
        return undefined;
    }
};
const getEffectiveToken = () => parseCVAccessToken() || currentToken;
const getRawAuthTokenRemainingLife = () => {
    const CVAccessToken = getEffectiveToken();
    if (!CVAccessToken) {
        return 0;
    }
    const { exp } = parseJwt(CVAccessToken);
    return new Date(exp * 1000).getTime() - Date.now();
};
const getAuthTokenRemainingLife = () => {
    const timeRemaining = getRawAuthTokenRemainingLife();
    return timeRemaining > 0 ? timeRemaining : 0;
};
const getParsedJwt = () => {
    const CVAccessToken = getEffectiveToken();
    if (!CVAccessToken) {
        return undefined;
    }
    return parseJwt(CVAccessToken);
};
const getExpiry = (exp) => new Date(exp * 1000);
const getValidSub = (parsedJwt) => {
    const { sub, exp } = parsedJwt;
    if (!sub || !exp) {
        return undefined;
    }
    return getExpiry(exp).getTime() > Date.now() ? sub : undefined;
};
const getUserId = () => {
    const parsedJwt = getParsedJwt();
    if (!parsedJwt) {
        return undefined;
    }
    return getValidSub(parsedJwt);
};
const getUserIdAndExpiration = () => {
    const parsedJwt = getParsedJwt();
    if (!parsedJwt) {
        return undefined;
    }
    const { exp } = parsedJwt;
    return { userId: getValidSub(parsedJwt), expires: getExpiry(exp).toUTCString() };
};
const hasAccessCookie = () => !!getEffectiveToken();
const hasLimitedAccessToken = () => {
    const cookie = getCookie('CVLimitedAccessToken');
    if (!cookie) {
        return false;
    }
    const expirationMatch = JSON.parse(cookie).expiration.match(/\d+/g);
    const expirationNumber = expirationMatch ? Number(expirationMatch[0]) : 0;
    const expirationDate = new Date(expirationNumber);
    return expirationDate > new Date();
};
const isLoggedIn = () => !!getUserId();
const hasLimitedOrFullAccessToken = () => isLoggedIn() || hasLimitedAccessToken();
const getBcId = () => getCookie('BrowserCookieId');
const saveUpdatedTokenCookie = (token, expires, validTime) => {
    const cvAccessToken = {
        access_token: token,
        expires_in: validTime,
        token_type: 'Bearer'
    };
    document.cookie = `CVAccessToken=${JSON.stringify(cvAccessToken)};Path=/;Expires=${expires.toUTCString()}`;
};
const updateToken = (token, handleError = () => { }) => {
    if (token && token.trim() !== '') {
        try {
            currentToken = token;
            const { exp, nbf } = parseJwt(token);
            saveUpdatedTokenCookie(token, getExpiry(exp), exp - nbf);
        }
        catch (err) {
            handleError(err);
        }
        currentToken = token;
    }
    else {
        currentToken = undefined;
    }
};
/**
 * Attempts to verify that the token is valid and available.
 *
 * By default verifyToken will wait for up to 5 seconds for the token to become valid
 * @param span used to record events about the token delay
 * @param options allows configuring the amount of time and attempts verify will wait for the token to become valid
 */
const verifyToken = (span, options) => new Promise((resolve, reject) => {
    var _a, _b;
    const delay = (_a = options === null || options === void 0 ? void 0 : options.delay) !== null && _a !== void 0 ? _a : 1000;
    const attempts = (_b = options === null || options === void 0 ? void 0 : options.attempts) !== null && _b !== void 0 ? _b : 5;
    const effectiveDelay = delay;
    const effectiveAttempts = attempts;
    const maxWait = (effectiveDelay / 1000) * effectiveAttempts;
    const remainingLife = () => ({
        authTokenRemainingLife: getRawAuthTokenRemainingLife()
    });
    const recheckToken = times => {
        if (getRawAuthTokenRemainingLife() > 0) {
            span.addEvent('VerifyTokenCompleted', Object.assign({}, remainingLife()));
            resolve(true);
            return;
        }
        span.addEvent('AuthTokenRemainingLife', Object.assign(Object.assign({ message: 'Checking auth token remaining life' }, remainingLife()), { remainingChecks: times }));
        if (times <= 0) {
            span.addEvent('RejectAuthTokenUpdate', Object.assign({ message: `Updated auth token not detected after ${maxWait} seconds` }, remainingLife()));
            reject(new Error(`Login token not detected after ${maxWait} seconds`));
            return;
        }
        setTimeout(() => {
            recheckToken(times - 1);
        }, effectiveDelay);
    };
    span.addEvent('VerifyTokenStarted', Object.assign({}, remainingLife()));
    recheckToken(effectiveAttempts);
});
exports.default = {
    deleteCookie,
    getBcId,
    getCookie,
    getAuthTokenRemainingLife,
    getEffectiveToken,
    getParsedJwt,
    getRawAuthTokenRemainingLife,
    getUserId,
    getUserIdAndExpiration,
    hasAccessCookie,
    hasLimitedAccessToken,
    hasLimitedOrFullAccessToken,
    isLoggedIn,
    parseCVAccessToken,
    setCookie,
    parseJwt,
    updateToken,
    verifyToken
};
